import type { ReactElement, ReactNode } from 'react'
import type { Appointment } from '@/types/clinic'

import Link from 'next/link'
import { useState } from 'react'
import { ClipboardPlus, MessageCircle, Pencil, RefreshCw } from 'lucide-react'
import { useClinic } from '@/lib/clinic/store'
import { formatTime, formatWeekdayDate, minutesToTime, timeToMinutes } from '@/lib/dates'
import { usePhoneFormatter } from '@/hooks/use-clinic-helpers'
import { useAppDialogs } from '@/components/dialogs/app-dialogs'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import PatientAvatar from '@/components/app/patient-avatar'
import AppointmentStatusMenu from '@/components/appointments/appointment-status-menu'

interface AppointmentPopoverProps {
    appointment: Appointment
    trigger: ReactElement
    children: ReactNode
}

// Calendar entries open this card with the booking details and the most used actions.
const AppointmentPopover = ({ appointment, trigger, children }: AppointmentPopoverProps): ReactNode => {
    const { data } = useClinic()
    const formatPhone = usePhoneFormatter()
    const { openAppointment, openVisit, openWhatsApp } = useAppDialogs()
    const [open, setOpen] = useState(false)

    const patient = data.patients.find(item => item.id === appointment.patientId)
    const hasVisit = data.visits.some(item => item.appointmentId === appointment.id)
    const end = minutesToTime(timeToMinutes(appointment.time) + appointment.duration)

    const run = (action: () => void) => {
        setOpen(false)
        window.setTimeout(action, 120)
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger render={trigger}>
                {children}
            </PopoverTrigger>
            <PopoverContent className='w-80 gap-3 p-4' align='start'>
                <div className='flex items-start gap-3'>
                    <PatientAvatar name={patient?.name ?? '?'} />
                    <div className='min-w-0 flex-1'>
                        {patient
                            ? <Link href={`/patients/${patient.id}`} className='block truncate text-sm font-semibold hover:text-primary hover:underline'>{patient.name}</Link>
                            : <p className='text-sm font-semibold'>Removed patient</p>}
                        <p className='text-xs text-muted-foreground'>{patient?.code} · {formatPhone(appointment.phone)}</p>
                    </div>
                </div>

                <dl className='grid gap-1.5 rounded-lg bg-muted/50 p-3 text-sm'>
                    <div className='flex justify-between gap-3'>
                        <dt className='text-muted-foreground'>When</dt>
                        <dd className='text-right font-medium'>{formatWeekdayDate(appointment.date)}, {formatTime(appointment.time)} to {formatTime(end)}</dd>
                    </div>
                    <div className='flex justify-between gap-3'>
                        <dt className='text-muted-foreground'>Treatment</dt>
                        <dd className='text-right font-medium'>{appointment.treatmentName || 'Not set'}</dd>
                    </div>
                    {appointment.notes && (
                        <div className='flex justify-between gap-3'>
                            <dt className='text-muted-foreground'>Notes</dt>
                            <dd className='text-right'>{appointment.notes}</dd>
                        </div>
                    )}
                </dl>

                <AppointmentStatusMenu appointment={appointment} />

                <div className='grid grid-cols-2 gap-2'>
                    <Button size='sm' variant='outline' onClick={() => run(() => openAppointment({ appointmentId: appointment.id }))}>
                        <Pencil aria-hidden='true' />
                        Edit
                    </Button>
                    <Button size='sm' variant='outline' onClick={() => run(() => openAppointment({ appointmentId: appointment.id, reschedule: true }))}>
                        <RefreshCw aria-hidden='true' />
                        Reschedule
                    </Button>
                    <Button size='sm' variant='outline' className='text-emerald-700' onClick={() => run(() => openWhatsApp({ patientId: appointment.patientId, kind: 'appointmentReminder', appointmentId: appointment.id }))}>
                        <MessageCircle aria-hidden='true' />
                        WhatsApp
                    </Button>
                    {!hasVisit && appointment.status !== 'cancelled' && (
                        <Button size='sm' variant='outline' onClick={() => run(() => openVisit({ patientId: appointment.patientId, appointmentId: appointment.id }))}>
                            <ClipboardPlus aria-hidden='true' />
                            Record visit
                        </Button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}

export default AppointmentPopover
